import { T, TF } from 'react-translator-component';

import {
  connectorBreakdown,
  CONNECTOR_STATUS,
  currentTypeLabel,
  formatDate,
  formatDateTime,
  formatKwh,
  formatMoney,
  formatPower,
  LOCATION_STATUS,
  SESSION_STATUS,
  sortByStartDesc,
  statusOf,
} from './ocpiHelpers';
import StatusTag from './StatusTag';
import * as S from './styled';

import { OcpiLocation, OcpiSession } from '../../../types/responses/Ocpi';

interface Props {
  location: OcpiLocation;
  sessions: OcpiSession[];
  onOpenSession: (pk: number) => void;
}

/** Conteúdo do drawer da estação enviada: o que o parceiro recebe dela e as recargas do período. */
const StationDetail = ({ location, sessions, onOpenSession }: Props) => {
  const connectors = location.connectors || [];
  const photos = location.chargePointPhotos || [];
  const hasCoordinates = location.locationLatitude != null && location.locationLongitude != null;
  const street = [location.street, location.houseNumber].filter(Boolean).join(', ');

  return (
    <div>
      <S.DetailCard>
        <S.DetailTitle>{T('Status')}</S.DetailTitle>
        <S.DetailRow>
          {T('Station')}
          <S.DetailValue>
            <StatusTag {...statusOf(LOCATION_STATUS, location.status)} />
          </S.DetailValue>
        </S.DetailRow>
        <S.DetailRow>
          {T('Connectors')}
          <S.DetailValue>{connectorBreakdown(connectors, TF) || '—'}</S.DetailValue>
        </S.DetailRow>
        <S.DetailHint>{T('The partners receive this station in /locations. In OCPI, each connector is published as an EVSE.')}</S.DetailHint>
      </S.DetailCard>

      <S.DetailCard>
        <S.DetailTitle>{T('Station')}</S.DetailTitle>
        <S.DetailRow>
          {T('Station ID')}
          <S.DetailValue>
            <S.DetailMono>{location.chargeBoxId}</S.DetailMono>
          </S.DetailValue>
        </S.DetailRow>
        <S.DetailRow>
          UUID
          <S.DetailValue>
            <S.DetailMono>{location.chargeBoxUuid || '—'}</S.DetailMono>
          </S.DetailValue>
        </S.DetailRow>
        <S.DetailRow>
          {T('Type')}
          <S.DetailValue>{location.typeOf === 'PUBLIC' ? T('Public') : location.typeOf || '—'}</S.DetailValue>
        </S.DetailRow>
        <S.DetailRow>
          {T('Registered on')}
          <S.DetailValue>{formatDate(location.latestTimestamp)}</S.DetailValue>
        </S.DetailRow>
        <S.DetailRow>
          {T('Photos')}
          <S.DetailValue>{photos.length ? photos.length : T('No photos')}</S.DetailValue>
        </S.DetailRow>
      </S.DetailCard>

      <S.DetailCard>
        <S.DetailTitle>{T('Location')}</S.DetailTitle>
        <S.DetailRow>
          {T('Address')}
          <S.DetailValue>{street || '—'}</S.DetailValue>
        </S.DetailRow>
        <S.DetailRow>
          {T('City')}
          <S.DetailValue>{[location.city, location.state].filter(Boolean).join(' / ') || '—'}</S.DetailValue>
        </S.DetailRow>
        {location.zipCode && (
          <S.DetailRow>
            {T('ZIP code')}
            <S.DetailValue>{location.zipCode}</S.DetailValue>
          </S.DetailRow>
        )}
        <S.DetailRow>
          {T('Coordinates')}
          <S.DetailValue>
            {hasCoordinates ? (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${location.locationLatitude},${location.locationLongitude}`}
                target="_blank"
                rel="noopener noreferrer"
                title={`${T('Open in Google Maps')}`}
              >
                <S.DetailMono>
                  {location.locationLatitude}, {location.locationLongitude}
                </S.DetailMono>
              </a>
            ) : (
              '—'
            )}
          </S.DetailValue>
        </S.DetailRow>
        <S.DetailRow>
          {T('Location (address_pk)')}
          <S.DetailValue>
            <S.DetailMono>{location.addressPk ?? '—'}</S.DetailMono>
          </S.DetailValue>
        </S.DetailRow>
      </S.DetailCard>

      {connectors.map((c) => {
        const hasError = c.lastStatus?.errorCode && c.lastStatus.errorCode !== 'NoError';
        return (
          <S.DetailCard key={c.connectorPk ?? c.connectorId}>
            <S.DetailTitle>
              {T('Connector')} {c.connectorId} · {c.connectorType || '—'}
            </S.DetailTitle>
            <S.DetailRow>
              {T('Status')}
              <S.DetailValue>
                <StatusTag {...statusOf(CONNECTOR_STATUS, c.lastStatus?.status)} />
              </S.DetailValue>
            </S.DetailRow>
            <S.DetailRow>
              {T('Since')}
              <S.DetailValue>{formatDateTime(c.lastStatus?.timeStamp)}</S.DetailValue>
            </S.DetailRow>
            <S.DetailRow>
              {T('Connector power')}
              <S.DetailValue>
                {currentTypeLabel(c.currentType)} · {formatPower(c.powerMax)}
              </S.DetailValue>
            </S.DetailRow>
            <S.DetailRow>
              {T('Per kWh')}
              <S.DetailValue>{c.moneyPerKilowattIncome ? formatMoney(c.moneyPerKilowattIncome) : '—'}</S.DetailValue>
            </S.DetailRow>
            <S.DetailRow>
              {T('Per minute')}
              <S.DetailValue>{c.moneyPerDurationIncome ? formatMoney(c.moneyPerDurationIncome) : '—'}</S.DetailValue>
            </S.DetailRow>
            <S.DetailRow>
              {T('Idle fee')}
              <S.DetailValue>
                {c.idleConnectorFee && c.idleFeeDefaultAmount ? formatMoney(c.idleFeeDefaultAmount) : T('No fee')}
              </S.DetailValue>
            </S.DetailRow>
            {hasError && (
              <S.DetailRow>
                {T('Error reported by the station')}
                <S.DetailValue style={{ color: '#a3231d' }}>
                  <S.DetailMono>{c.lastStatus?.errorCode}</S.DetailMono>
                </S.DetailValue>
              </S.DetailRow>
            )}
          </S.DetailCard>
        );
      })}

      <S.DetailCard>
        <S.DetailTitle>
          {T('Charges at this station in the period')} ({sessions.length})
        </S.DetailTitle>
        {sessions.length === 0 && <S.DetailHint>{T('No charges at this station in the period.')}</S.DetailHint>}
        {sortByStartDesc(sessions).map((s) => (
          <S.DetailRow key={s.transactionPk} className="clickable" onClick={() => onOpenSession(s.transactionPk)}>
            <span>
              <S.DetailMono>#{s.transactionPk}</S.DetailMono> · {formatDateTime(s.startTimestamp)}
              <div>
                <StatusTag {...statusOf(SESSION_STATUS, s.status)} />
              </div>
            </span>
            <S.DetailValue>
              <div>{formatKwh(s.kwh)}</div>
              <div>{formatMoney(s.income, s.currency)}</div>
            </S.DetailValue>
          </S.DetailRow>
        ))}
      </S.DetailCard>
    </div>
  );
};

export default StationDetail;
